import { faBarcode, faShoppingCart } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PickingStep } from '../../hooks';
import { useToast } from '@shared/hooks/use-toast';

interface ScannerInputProps {
  currentStep: PickingStep;
  stepDescription: string;
  onScan: (code: string) => { success: boolean; message: string } | Promise<{ success: boolean; message: string }>;
  disabled?: boolean;
  cartCode?: string; // Nuevo prop para mostrar el carrito
}

export const ScannerInput: React.FC<ScannerInputProps> = ({ currentStep, onScan, disabled = false, cartCode }) => {
  const { toast } = useToast();
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const allowedScannerCharacterRegex = /[a-zA-Z0-9\-_.\s/\\@#$%&*+=()[\]{}|:;"'<>,?!~`]/;

  const isEditableElement = (element: Element | null): element is HTMLElement => {
    if (!(element instanceof HTMLElement)) return false;

    const tagName = element.tagName;
    return element.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
  };

  // Auto-focus inicial
  useEffect(() => {
    if (disabled) return;

    // Solo focus inicial al cargar el componente
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled, currentStep]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();

      if (!inputValue.trim() || isLoading) return;

      try {
        setIsLoading(true);

        const result = await onScan(inputValue.trim());

        toast({
          title: result.success ? 'Éxito' : 'Error',
          description: result.message,
          variant: result.success ? 'default' : 'destructive',
        });

        // Limpiar el input después del escaneo
        setInputValue('');
      } catch (error) {
        console.error('❌ ScannerInput - Error capturado:', error);
        toast({
          title: 'Error',
          description: 'Error al procesar el código escaneado',
          variant: 'destructive',
        });
        setInputValue('');
      } finally {
        setIsLoading(false);
      }
    },
    [inputValue, isLoading, onScan, toast],
  );

  // Capturar teclado globalmente
  useEffect(() => {
    if (disabled || isLoading) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const currentHasFocus = inputRef.current === document.activeElement;

      const activeElement = document.activeElement;
      const isTypingInAnotherEditableField = activeElement !== inputRef.current && isEditableElement(activeElement);

      // Evita robar foco o teclas cuando el usuario escribe en otro input,
      // como el StepperInput numérico usado para confirmar cantidades.
      if (isTypingInAnotherEditableField) {
        return;
      }

      // Permitir letras con Shift (códigos de barras suelen usar mayúsculas)
      // Solo ignorar si hay modificadores Y NO es una letra/número válido
      if (
        e.ctrlKey ||
        e.metaKey ||
        e.altKey ||
        (e.shiftKey && !(e.key.length === 1 && allowedScannerCharacterRegex.test(e.key)))
      ) {
        return;
      }

      // Capturar Enter - Envío inmediato
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
        return;
      }

      // Capturar Escape para limpiar
      if (e.key === 'Escape') {
        e.preventDefault();
        setInputValue('');
        return;
      }

      // Capturar caracteres válidos para códigos de barras
      if (e.key.length === 1 && allowedScannerCharacterRegex.test(e.key)) {
        e.preventDefault();

        // Auto-focus si no lo tiene
        if (!currentHasFocus && inputRef.current) {
          inputRef.current.focus();
        }

        const newValue = inputValue + e.key;
        setInputValue(newValue);
      }

      // Capturar Backspace
      if (e.key === 'Backspace') {
        e.preventDefault();
        setInputValue((prev) => prev.slice(0, -1));
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown, true);
  }, [disabled, handleSubmit, inputValue, isLoading]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const getPlaceholder = () => {
    switch (currentStep) {
      case 'cart':
        return 'Escanear código de carrito...';
      case 'location':
        return 'Escanear código de ubicación...';
      case 'product':
        return 'Escanear código de producto...';
      default:
        return 'Escanear código...';
    }
  };

  return (
    <div className="space-y-4 scanner-container">
      {/* Carrito y input en la misma línea - carrito a la izquierda */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Cart badge - a la izquierda, misma altura que el input */}
        {cartCode && (
          <div className="flex items-stretch">
            <div className="inline-flex items-center h-[40px] bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 rounded-full border border-blue-200 dark:border-blue-800 transition-colors">
              <FontAwesomeIcon icon={faShoppingCart} className="w-4 h-4 mr-2" />
              <span className="font-medium text-sm whitespace-nowrap">{cartCode}</span>
            </div>
          </div>
        )}

        {/* Input container */}
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
            <FontAwesomeIcon icon={faBarcode} className="w-4 h-4" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            disabled={disabled || isLoading}
            placeholder={isLoading ? 'Procesando...' : getPlaceholder()}
            className={`w-full pl-10 pr-4 py-2 text-base border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors h-[40px] ${
              disabled || isLoading
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-background text-foreground'
            }`}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
    </div>
  );
};
